// Localized AI-disclosure text for the widget. Always shown (EU AI Act
// Art. 50 transparency). The widget's locale comes from a per-tenant
// setting (widget_locale, default pl), with an explicit lang attribute
// on the embed as a per-embed override. <html lang>/navigator sniffing
// is an opt-in tenant choice (widget_locale === "auto"), NOT the
// default — see resolveLocale() below.

export type WidgetLocale = "pl" | "en" | "no";

const DEFAULTS: Record<WidgetLocale, string> = {
  pl: "Rozmawiasz z asystentem AI.",
  en: "You're chatting with an AI assistant.",
  no: "Du chatter med en AI-assistent.",
};

function mapLang(raw: string | null | undefined): WidgetLocale | null {
  if (!raw) return null;
  const two = raw.trim().slice(0, 2).toLowerCase();
  if (two === "pl") return "pl";
  if (two === "en") return "en";
  if (two === "no" || two === "nb" || two === "nn") return "no";
  return null;
}

// resolveLocale resolution order:
//   1. explicit lang attr on <chatmerce-chat> (per-embed override) — always wins;
//   2. tenant widget_locale: a concrete pl/en/no is used directly;
//      "auto" ⇒ <html lang> → navigator.language;
//   3. fallback pl.
// So <html lang>/navigator are consulted ONLY when the tenant opts into
// "auto". The default (pl/empty) never sniffs them — this is what fixes
// the dogid.pl bug (SPA shell ships <html lang="en"> unconditionally).
export function resolveLocale(sig: {
  attr?: string | null; // <chatmerce-chat lang="…">
  tenant?: string | null; // widget_locale: pl|en|no|auto|""
  htmlLang?: string | null; // only used when tenant === "auto"
  navLang?: string | null; // only used when tenant === "auto"
}): WidgetLocale {
  const fromAttr = mapLang(sig.attr);
  if (fromAttr) return fromAttr;
  if ((sig.tenant ?? "").trim().toLowerCase() === "auto") {
    return mapLang(sig.htmlLang) ?? mapLang(sig.navLang) ?? "pl";
  }
  return mapLang(sig.tenant) ?? "pl";
}

export function disclosureText(locale: WidgetLocale, override?: string | null): string {
  const o = (override ?? "").trim();
  return o || DEFAULTS[locale];
}

// Composer placeholder. The field used to render empty, which reads as
// unfinished now that it sits in an island of its own rather than behind
// a "Send" label that explained what the row was for.
const PLACEHOLDER: Record<WidgetLocale, string> = {
  pl: "Napisz wiadomość",
  en: "Type a message",
  no: "Skriv en melding",
};

export function composerPlaceholder(locale: WidgetLocale): string {
  return PLACEHOLDER[locale];
}

const FALLBACK_GREETING: Record<WidgetLocale, (agentName: string, brandName: string) => string> = {
  pl: (agentName, brandName) => `Cześć! Jestem ${agentName} z ${brandName}. W czym mogę pomóc?`,
  en: (agentName, brandName) => `Hi! I'm ${agentName} from ${brandName}. How can I help?`,
  no: (agentName, brandName) =>
    `Hei! Jeg er ${agentName} fra ${brandName}. Hvordan kan jeg hjelpe deg?`,
};

// fallbackGreeting builds the widget's default first-message greeting,
// localized to the resolved widget locale, used only when the tenant
// hasn't configured a custom welcome string (see ChatmerceChat.greet()).
export function fallbackGreeting(
  locale: WidgetLocale,
  agentName: string,
  brandName: string,
): string {
  return FALLBACK_GREETING[locale](agentName, brandName);
}

// TRANSPORT_ERROR are the two client-side failure bubbles. They were the
// last hardcoded Polish strings in the widget: a rate-limited or offline
// English visitor got a Polish error even with lang="en" on the embed.
const TRANSPORT_ERROR: Record<WidgetLocale, { rateLimited: string; generic: string }> = {
  pl: {
    rateLimited: "Zbyt wiele wiadomości — spróbuj za chwilę.",
    generic: "Coś poszło nie tak. Spróbuj ponownie za moment.",
  },
  en: {
    rateLimited: "Too many messages — try again in a moment.",
    generic: "Something went wrong. Please try again in a moment.",
  },
  no: {
    rateLimited: "For mange meldinger — prøv igjen om litt.",
    generic: "Noe gikk galt. Prøv igjen om et øyeblikk.",
  },
};

// transportErrorText returns the failure bubble for the resolved locale.
export function transportErrorText(locale: WidgetLocale, rateLimited: boolean): string {
  const t = TRANSPORT_ERROR[locale];
  return rateLimited ? t.rateLimited : t.generic;
}
